import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Grid, Typography, IconButton, useTheme } from '@material-ui/core';
import { subDays, differenceInDays, addDays, startOfDay } from 'date-fns';
import { FaShareSquare } from 'react-icons/fa';
import { ToastContainer } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/auth';
import { usePreferences } from '../../hooks/preferences';
import { faLogEvent } from '../../services/firebase/analytics';

import InfoCardWithChart from '../../components/cards/InfoCardWithChart';
import PieChart from '../../components/charts/PieChart';
import UnitChart from '../../components/charts/UnitChart';
import CustomDropdownAlt from '../../components/selectors/CustomDropdownAlt';
import CreateOrderButton from '../../components/buttons/CreateOrderButton';
import DatePicker from '../../components/selectors/DatePicker';
import NoDataMessage from '../../components/utils/NoDataMessage';
import SingleBarChart from '../../components/charts/SingleBarChart';
import AverageQualityChart from '../../components/charts/AverageQualityChart';
import RoadsDegradationChart from '../../components/charts/RoadsDegradationChart';

import { IState } from '../../store';
import { IOrdersState } from '../../store/modules/orders/types';
import { IKpisState } from '../../store/modules/kpis/types';
import { getKpisRequest } from '../../store/modules/kpis/actions';

import {
  calculateAverageRoadCondition,
  calculateDfByInterval,
  calculateExecutionRate,
  calculateOrdersStatisticsByInterval,
  handleMissingData,
} from '../../utils/getChartsValues';

import { IOrderDetailed } from '../../types/orders';

import { useStyles } from './styles';

interface ICardsInfo {
  chartData: number[];
  increase: number;
}

interface IKpisChartData {
  status: string;
  data: number[];
}

interface IRegionsRanking {
  region: string | null;
  count: number;
}

interface IRoadsRanking {
  road: string | null;
  count: number;
}

interface ISerie {
  name: string;
  data: number[];
}

const AssetsHome: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user, jwt, selectUnit, selectedUnit } = useAuth();
  const { disableTabs, selectDataType } = usePreferences();
  const orders = useSelector<IState, IOrdersState>(state => state.orders);
  const kpisState = useSelector<IState, IKpisState>(appState => appState.kpis);
  const [openOrders, setOpenOrders] = useState<IOrderDetailed[]>([]);
  const [finishedOrders, setFinishedOrders] = useState<IOrderDetailed[]>([]);
  const history = useHistory();
  const [unitOpenOrders, setUnitOpenOrders] = useState<IOrderDetailed[]>([]);
  const [unitFinishedOrders, setUnitFinishedOrders] = useState<
    IOrderDetailed[]
  >([]);
  const [openSelectUnit, setOpenSelectUnit] = useState(false);
  const [userUnits, setUserUnits] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [dayInterval, setDayInterval] = useState(7);
  const [backlogInfo, setBacklogInfo] = useState<ICardsInfo>({
    chartData: [],
    increase: 0,
  });
  const [mttrInfo, setMttrInfo] = useState({
    value: 0,
    meta: 0,
  });
  const [mtbfInfo, setMtbfInfo] = useState({
    value: 0,
    meta: 0,
  });
  const [df, setDf] = useState<number>(0);
  const [dfData, setDfData] = useState<number[]>([]);
  const [roadConditionsData, setRoadConditionsData] = useState<number[]>([]);
  const [mttrChartData, setMttrChartData] = useState<IKpisChartData>({
    status: '',
    data: [],
  });
  const [mtbfChartData, setMtbfChartData] = useState<IKpisChartData>({
    status: '',
    data: [],
  });
  const [openOrdersRegionRanking, setOpenOrdersRegionRanking] = useState<
    IRegionsRanking[]
  >([]);
  const [openOrdersRoadsRanking, setOpenOrdersRoadsRanking] = useState<
    IRoadsRanking[]
  >([]);
  const [executionRateData, setExecutionRateData] = useState<ISerie[]>([]);

  const handleClearDates = (): void => {
    setStartDate(null);
    setEndDate(null);
  };

  const getSelectedUnitData = useCallback(() => {
    if (selectedUnit.id === 'itabira') {
      setUnitOpenOrders(openOrders);
      setUnitFinishedOrders(finishedOrders);
    } else {
      const unitData = user.units.find(unit => unit.name === selectedUnit.name);
      if (unitData) {
        setUnitOpenOrders(
          openOrders.filter(order => order.unit_id === unitData.id),
        );
        setUnitFinishedOrders(
          finishedOrders.filter(order => order.unit_id === unitData.id),
        );
      }
    }
  }, [selectedUnit, openOrders, finishedOrders, user.units]);

  const openOrdersAmount = useMemo(() => {
    const values = [];

    if (startDate && endDate) {
      const filteredOpenOrders = unitOpenOrders.filter(
        order =>
          new Date(order.timestamp) >= startDate &&
          new Date(order.timestamp) <= endDate,
      );
      const totalUrgent = filteredOpenOrders.filter(
        pnt => pnt.priority === 'URGENTE',
      ).length;
      const totalHigh = filteredOpenOrders.filter(
        order => order.priority === 'ALTA',
      ).length;
      const totalAverage = filteredOpenOrders.filter(
        order => order.priority === 'MEDIA',
      ).length;
      const totalLow = filteredOpenOrders.filter(
        order => order.priority === 'BAIXA',
      ).length;

      values.push(totalLow);
      values.push(totalAverage);
      values.push(totalHigh);
      values.push(totalUrgent);
    } else {
      const totalUrgent = unitOpenOrders.filter(
        pnt => pnt.priority === 'URGENTE',
      ).length;
      const totalHigh = unitOpenOrders.filter(
        order => order.priority === 'ALTA',
      ).length;
      const totalAverage = unitOpenOrders.filter(
        order => order.priority === 'MEDIA',
      ).length;
      const totalLow = unitOpenOrders.filter(
        order => order.priority === 'BAIXA',
      ).length;

      values.push(totalLow);
      values.push(totalAverage);
      values.push(totalHigh);
      values.push(totalUrgent);
    }

    return values;
  }, [unitOpenOrders, startDate, endDate]);

  const averageRoadCondition = useMemo(() => {
    if (roadConditionsData.length > 0) {
      const sumRoadConditions = roadConditionsData.reduce((a, b) => a + b);
      const average = sumRoadConditions / roadConditionsData.length;
      return average;
    }
    return -1;
  }, [roadConditionsData]);

  const dfIncrease = useMemo(() => {
    if (dfData.length > 0) {
      if (dfData[0] > 0) {
        const increase = ((dfData[-1] - dfData[0]) * 100) / dfData[0];
        return increase;
      }
      return 0;
    }
    return 0;
  }, [dfData]);

  const mtbfIncrease = useMemo(() => {
    if (mtbfChartData.data.length > 0) {
      if (mtbfChartData.data[0] > 0) {
        const increase =
          ((mtbfChartData.data[-1] - mtbfChartData.data[0]) * 100) /
          mtbfChartData.data[0];
        return increase;
      }
      return 0;
    }
    return 0;
  }, [mtbfChartData]);

  const mttrIncrease = useMemo(() => {
    if (mttrChartData.data.length > 0) {
      if (mttrChartData.data[0] > 0) {
        const increase =
          ((mttrChartData.data[-1] - mttrChartData.data[0]) * 100) /
          mttrChartData.data[0];
        return increase;
      }
      return 0;
    }
    return 0;
  }, [mttrChartData]);

  const roadConditionsIncrease = useMemo(() => {
    if (roadConditionsData.length > 0) {
      if (roadConditionsData[0] > 0) {
        const increase =
          ((roadConditionsData[-1] - roadConditionsData[0]) * 100) /
          roadConditionsData[0];
        return increase;
      }
      return 0;
    }
    return 0;
  }, [roadConditionsData]);

  const handleCalculateRegionsRanking = useCallback(() => {
    const regionsOpenOrders = [
      ...Array.from(new Set(unitOpenOrders.map(order => order.region))),
    ];

    const openOrdersByRegion = [];

    for (let i = 0; i < regionsOpenOrders.length; i += 1) {
      const regionOrders = unitOpenOrders.filter(
        order => order.region === regionsOpenOrders[i],
      );
      openOrdersByRegion.push(regionOrders.length);
    }

    const openOrdersRankingData = [];
    for (let i = 0; i < openOrdersByRegion.length; i += 1) {
      openOrdersRankingData.push({
        region: regionsOpenOrders[i],
        count: openOrdersByRegion[i],
      });
    }

    const openOrdersSortedRanking = openOrdersRankingData.sort(
      (a, b) => b.count - a.count,
    );
    setOpenOrdersRegionRanking(openOrdersSortedRanking);
  }, [unitOpenOrders]);

  const handleCalculateRoadsRanking = useCallback(() => {
    const roadsOpenOrders = [
      ...Array.from(new Set(unitOpenOrders.map(order => order.road))),
    ];

    const openOrdersByRoad = [];

    for (let i = 0; i < roadsOpenOrders.length; i += 1) {
      const roadOrders = unitOpenOrders.filter(
        order => order.road === roadsOpenOrders[i],
      );
      openOrdersByRoad.push(roadOrders.length);
    }

    const openOrdersRankingData = [];
    for (let i = 0; i < openOrdersByRoad.length; i += 1) {
      openOrdersRankingData.push({
        road: roadsOpenOrders[i],
        count: openOrdersByRoad[i],
      });
    }

    const openOrdersSortedRanking = openOrdersRankingData.sort(
      (a, b) => b.count - a.count,
    );
    setOpenOrdersRoadsRanking(openOrdersSortedRanking);
  }, [unitOpenOrders]);

  const handleSelectUnit = useCallback(
    (unitName: string) => {
      if (unitName.toLowerCase() === 'complexo de itabira') {
        selectUnit('itabira');
        return;
      }
      const selected = user.units.find(unit => unit.name === unitName);
      if (selected) {
        selectUnit(selected.id);
      }
    },
    [selectUnit, user.units],
  );

  const handleCalculateExecutionRate = useCallback((): void => {
    if (startDate && endDate) {
      const result = calculateExecutionRate(
        unitOpenOrders,
        unitFinishedOrders,
        startDate,
        endDate,
      );
      setExecutionRateData(result.executionRateData);
    } else {
      const sortedOrders = openOrders.sort((a, b) => {
        return Number(b.code) - Number(a.code);
      });
      const lastDay = addDays(
        startOfDay(new Date(sortedOrders[0].timestamp)),
        14,
      );
      const firstDay = subDays(lastDay, 28);
      const result = calculateExecutionRate(
        unitOpenOrders,
        unitFinishedOrders,
        firstDay,
        lastDay,
      );
      setExecutionRateData(result.executionRateData);
    }
  }, [openOrders, unitOpenOrders, unitFinishedOrders, startDate, endDate]);

  const handleClickCreateOrder = (): void => {
    faLogEvent('CREATE_ORDER_FROM_ASSETS_HOME_SCREEN');
    history.push('/dashboard/monitoramento/ordens-de-servico', {
      shouldCreateOrder: true,
    });
  };

  // GET ORDERS FROM STATE
  useEffect(() => {
    if (orders) {
      const data = [...orders.openOrders, ...orders.incompleteOrders];
      setOpenOrders(data);
    }

    if (orders.finishedOrders.length > 0) {
      setFinishedOrders(orders.finishedOrders);
    }
  }, [orders]);

  // SET SELECTED UNIT
  useEffect(() => {
    if (user.units.length > 0) {
      const unitsNames = user.units.map(unit => unit.name);
      setUserUnits(unitsNames);
    }
  }, [user.units]);

  // FILTER DATA BY DATE AND CALCULATE KPIS
  useEffect(() => {
    if (startDate && endDate) {
      const numberOfDays = differenceInDays(endDate, startDate);
      setDayInterval(numberOfDays);
      dispatch(
        getKpisRequest({
          user,
          jwt,
          startDate,
          endDate,
          timescale: 'week',
        }),
      );
    } else {
      const numberOfDays = 7;
      setDayInterval(numberOfDays);
      const today = new Date();
      const startDay = subDays(today, numberOfDays);
      dispatch(
        getKpisRequest({
          user,
          jwt,
          startDate: startDay,
          endDate: today,
          timescale: 'week',
        }),
      );
    }
  }, [startDate, endDate, user, jwt, dispatch]);

  // OPEN ORDERS CHARTS VALUES
  useEffect(() => {
    const today = new Date();
    const startDay = subDays(today, 7);
    const unitData = user.units.find(unit => unit.name === selectedUnit.name);
    if (unitData) {
      const selectedOpenOrders = openOrders.filter(
        order => order.unit_id === unitData.id,
      );
      if (selectedOpenOrders.length === 0) {
        setBacklogInfo({
          chartData: [],
          increase: 0,
        });
      } else {
        const backlogResult = calculateOrdersStatisticsByInterval(
          selectedOpenOrders,
          startDay,
          today,
        );

        setBacklogInfo({
          chartData: backlogResult.chartData,
          increase: backlogResult.ordersCreationIncrease,
        });
      }
    }
  }, [openOrders, selectedUnit, user.units]);

  // MTBF, MTTR AND ROAD CONDITIONS CHARTS VALUES
  useEffect(() => {
    if (kpisState.kpis.length > 0) {
      const unitData = user.units.find(unit => unit.name === selectedUnit.name);
      if (unitData) {
        const selectedKpiData = kpisState.kpis.find(
          kpi => kpi.unit_id === unitData.id,
        );
        if (selectedKpiData) {
          const formattedMttr = handleMissingData(selectedKpiData.mttr);
          const formattedMtbf = handleMissingData(selectedKpiData.mtbf);
          const averageRoadConditions = calculateAverageRoadCondition(
            selectedKpiData,
          );
          setMttrChartData({
            status: formattedMttr.status,
            data: formattedMttr.formattedData,
          });
          setMtbfChartData({
            status: formattedMtbf.status,
            data: formattedMtbf.formattedData,
          });
          setRoadConditionsData(averageRoadConditions);
        } else {
          setMttrChartData({
            status: '',
            data: [],
          });
          setMtbfChartData({
            status: '',
            data: [],
          });
          setRoadConditionsData([]);
        }
      }
    }
  }, [kpisState.kpis, user.units, selectedUnit]);

  // MTBF, MTTR AND DF CARDS VALUES
  useEffect(() => {
    if (kpisState.averages.length > 0) {
      const unitData = user.units.find(unit => unit.name === selectedUnit.name);
      if (unitData) {
        const selectedKpiData = kpisState.averages.find(
          kpi => kpi.unit_id === unitData.id,
        );
        setMttrInfo({
          value: selectedKpiData ? selectedKpiData.averageMttr : 0,
          meta: selectedKpiData ? selectedKpiData.averageMttr : 0,
        });
        setMtbfInfo({
          value: selectedKpiData ? selectedKpiData.averageMtbf : 0,
          meta: selectedKpiData ? selectedKpiData.averageMtbf : 0,
        });
        setDf(selectedKpiData ? selectedKpiData.averageDf : 0);
      }
    }
  }, [kpisState.averages, user.units, kpisState.kpis, selectedUnit]);

  // REGIONS/ROADS RANK
  useEffect(() => {
    handleCalculateRegionsRanking();
    handleCalculateRoadsRanking();
  }, [handleCalculateRegionsRanking, handleCalculateRoadsRanking]);

  // SELECTED UNIT DF
  useEffect(() => {
    const unitData = user.units.find(unit => unit.name === selectedUnit.name);
    if (unitData) {
      setDfData(calculateDfByInterval(kpisState.kpis, unitData.id));
    }
  }, [kpisState.kpis, selectedUnit, user.units]);

  useEffect(() => {
    if (openOrders.length > 0 && finishedOrders.length > 0) {
      handleCalculateExecutionRate();
    }
  }, [openOrders, finishedOrders, handleCalculateExecutionRate]);

  useEffect(() => {
    if (openOrders.length > 0) {
      getSelectedUnitData();
    }
  }, [openOrders, getSelectedUnitData]);

  // HANDLE ASSETS TAB BAR INITIAL VALUES
  useEffect(() => {
    selectDataType('roads');
    disableTabs({
      roads: false,
      trucks: true,
      drills: true,
    });
  }, [selectDataType, disableTabs]);

  return (
    <div className={classes.container}>
      <ToastContainer
        style={{ position: 'absolute', top: 64, zIndex: 99999999999 }}
      />
      <header className={classes.header}>
        <CustomDropdownAlt
          open={openSelectUnit}
          text={selectedUnit.name}
          labels={['Complexo de Itabira', ...userUnits]}
          toggle={() => setOpenSelectUnit(!openSelectUnit)}
          onClickItem={(event: any) => handleSelectUnit(event.target.innerText)}
          height={48}
        />
        <CreateOrderButton
          height={48}
          onClick={() => handleClickCreateOrder()}
        />
        <DatePicker
          id="dashboard-datepicker"
          startDate={startDate}
          endDate={endDate}
          onChangeStartDate={(date: Date | null) => setStartDate(date)}
          onChangeEndDate={(date: Date | null) => setEndDate(date)}
          onClickClearDate={handleClearDates}
          height={48}
          width={300}
          marginBottom={0}
          fontSize={14}
        />
      </header>
      <Grid container className={classes.content}>
        {/* ORDERS */}
        <Grid
          id="orders-container"
          item
          xs={12}
          sm={6}
          md={4}
          lg={4}
          className={classes.grid}
          style={{ paddingLeft: 16 }}
        >
          <div className={classes.sectionContainer}>
            <div className={classes.sectionTitleContainer}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {t('screens:assetsHome.orders')}
              </Typography>
              <IconButton
                aria-label={t('screens:assetsHome.ordersAria')}
                onClick={() =>
                  history.push('/dashboard/monitoramento/ordens-de-servico')
                }
                className={classes.sectionButton}
              >
                <FaShareSquare size={24} color="#FFF" />
              </IconButton>
            </div>
            <section className={classes.sectionContent}>
              {openOrdersAmount && (
                <InfoCardWithChart
                  title={t('screens:assetsHome.openOrders')}
                  value={openOrdersAmount.reduce((a, b) => a + b, 0)}
                  deltaValue={backlogInfo.increase}
                  deltaUnit="%"
                  deltaPrecision={1}
                  chartData={backlogInfo.chartData}
                  numberOfDays={dayInterval}
                />
              )}
              {openOrdersAmount.reduce((a, b) => a + b, 0) > 0 ? (
                <PieChart
                  values={openOrdersAmount}
                  title={t('screens:assetsHome.priorityOfOrders')}
                  showLegend={false}
                  titleWidth="100%"
                />
              ) : (
                <NoDataMessage
                  title={t('screens:assetsHome.priorityOfOrders')}
                />
              )}
              {executionRateData.length > 0 && (
                <SingleBarChart
                  title={t('screens:assetsHome.executionRate')}
                  data={[
                    {
                      value: executionRateData[0].data.reduce((a, b) => a + b),
                      color: theme.palette.primary.main,
                      label: executionRateData[0].name,
                    },
                    {
                      value: executionRateData[1].data.reduce((a, b) => a + b),
                      color: theme.palette.primary.light,
                      label: executionRateData[1].name,
                    },
                    {
                      value: executionRateData[2].data.reduce((a, b) => a + b),
                      color: '#C4C9D0',
                      label: executionRateData[2].name,
                    },
                  ]}
                />
              )}
              <div className={classes.rankingContainer}>
                <Typography
                  variant="subtitle1"
                  className={classes.lastSectionTitle}
                >
                  {t('screens:assetsHome.openOrdersRanking')}
                </Typography>
                <div className={classes.rankingCharts}>
                  <div className={classes.rankingContent}>
                    <Typography variant="subtitle1">
                      {t('screens:assetsHome.regions')}
                    </Typography>
                    {openOrdersRegionRanking[0] && (
                      <UnitChart
                        title={
                          openOrdersRegionRanking[0].region ||
                          t('components:charts.noName')
                        }
                        value={openOrdersRegionRanking[0].count || 0}
                        variant="dark"
                      />
                    )}
                    {openOrdersRegionRanking[1] && (
                      <UnitChart
                        title={
                          openOrdersRegionRanking[1]?.region ||
                          t('components:charts.noName')
                        }
                        value={openOrdersRegionRanking[1]?.count || 0}
                        variant="dark"
                      />
                    )}
                    {openOrdersRegionRanking[2] && (
                      <UnitChart
                        title={
                          openOrdersRegionRanking[2]?.region ||
                          t('components:charts.noName')
                        }
                        value={openOrdersRegionRanking[2]?.count || 0}
                        variant="dark"
                      />
                    )}
                  </div>
                  <div className={classes.rankingContent}>
                    <Typography variant="subtitle1">Pistas</Typography>
                    {openOrdersRoadsRanking[0] && (
                      <UnitChart
                        title={
                          openOrdersRoadsRanking[0].road ||
                          t('components:charts.noName')
                        }
                        value={openOrdersRoadsRanking[0].count || 0}
                        variant="light"
                      />
                    )}
                    {openOrdersRoadsRanking[1] && (
                      <UnitChart
                        title={
                          openOrdersRoadsRanking[1]?.road ||
                          t('components:charts.noName')
                        }
                        value={openOrdersRoadsRanking[1]?.count || 0}
                        variant="light"
                      />
                    )}
                    {openOrdersRoadsRanking[2] && (
                      <UnitChart
                        title={
                          openOrdersRoadsRanking[2]?.road ||
                          t('components:charts.noName')
                        }
                        value={openOrdersRoadsRanking[2]?.count || 0}
                        variant="light"
                      />
                    )}
                  </div>
                </div>
              </div>
            </section>
          </div>
        </Grid>

        {/* AVAILABILITY */}
        <Grid
          item
          xs={12}
          sm={6}
          md={4}
          lg={4}
          className={classes.grid}
          style={{
            paddingRight: 16,
          }}
        >
          <div className={classes.sectionContainer}>
            <div className={classes.sectionTitleContainer}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {t('screens:assetsHome.availability')}
              </Typography>
              <IconButton
                aria-label={t('screens:assetsHome.availabilityAria')}
                onClick={() =>
                  history.push('/dashboard/monitoramento/disponibilidade')
                }
                className={classes.sectionButton}
              >
                <FaShareSquare size={24} color="#FFF" />
              </IconButton>
            </div>
            <section className={classes.sectionContent}>
              <InfoCardWithChart
                title={`${t('screens:assetsHome.df')} (%)`}
                value={df}
                precision={1}
                deltaValue={dfIncrease}
                deltaUnit="%"
                deltaPrecision={1}
                chartData={dfData}
                numberOfDays={dayInterval}
              />
              <InfoCardWithChart
                title="MTBF (H)"
                value={mtbfInfo.value}
                precision={1}
                deltaValue={mtbfIncrease}
                deltaUnit="%"
                deltaPrecision={1}
                chartData={mtbfChartData.data}
                numberOfDays={dayInterval}
              />
              <InfoCardWithChart
                title="MTTR (H)"
                value={mttrInfo.value}
                precision={1}
                deltaValue={mttrIncrease}
                deltaUnit="%"
                deltaPrecision={1}
                chartData={mttrChartData.data}
                numberOfDays={dayInterval}
              />
              <InfoCardWithChart
                title={t('screens:assetsHome.roadsConditions')}
                value={averageRoadCondition === 0 ? -1 : averageRoadCondition}
                precision={1}
                deltaValue={roadConditionsIncrease}
                deltaUnit="%"
                deltaPrecision={1}
                chartData={roadConditionsData}
                numberOfDays={dayInterval}
              />
            </section>
          </div>
        </Grid>

        {/* QUALITY */}
        <Grid item xs={12} sm={6} md={4} lg={4} className={classes.grid}>
          <div className={classes.sectionContainer}>
            <div className={classes.sectionTitleContainer}>
              <Typography variant="subtitle1" className={classes.sectionTitle}>
                {t('screens:assetsHome.quality')}
              </Typography>
              <IconButton
                aria-label={t('screens:assetsHome.qualityAria')}
                // onClick={() => history.push('/dashboard/monitoramento/qualidade')}
                className={classes.sectionButton}
              >
                <FaShareSquare size={24} color="#FFF" />
              </IconButton>
            </div>
            <section className={classes.sectionContent}>
              <AverageQualityChart
                series={[24, 23, 52, 55]}
                value={100}
                duration={2}
              />
              <Typography variant="subtitle1" className={classes.title}>
                {t('screens:assetsHome.roadsDegradationTime')}
              </Typography>
              <RoadsDegradationChart />
              {/* <NewFailureRiskChart /> */}
            </section>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default AssetsHome;
